import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api';

/**
 * Account 360 — read-only unified detail page.
 *
 * Layout:
 *   - Header: name, status pill, ALN pill, "Back to Accounts"
 *   - Identity row: address, region, mgmt co, sales rep
 *   - Capability map: shows which panels are LIVE and which are LOCKED
 *     (status-gated). Locked panels render in a muted card so users
 *     immediately understand WHY a panel is empty/disabled.
 *   - 30-day operational rollup (active customers only)
 *   - Linkage panel: shows which legacy IDs this account is wired to
 *     (legacy_property_id, directory_uuid, aln_id) — the migration's
 *     audit story for super-admin during the cutover period.
 *   - Coming-soon stubs: Notes, Activities, To-dos, Meetings (CRM
 *     features that arrive in subsequent steps).
 *
 * WRITE SAFETY: zero. This page only reads.
 */
export default function AccountDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notEnabled, setNotEnabled] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await api.getAccount360(id);
        if (mounted) setBundle(data);
      } catch (e) {
        if (!mounted) return;
        if (/not enabled/i.test(e.message)) setNotEnabled(true);
        else setError(e.message || 'Failed to load');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  if (notEnabled) {
    return (
      <div className="admin-page">
        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: 16, color: '#92400e' }}>
          The unified Accounts view is not enabled yet.
        </div>
      </div>
    );
  }
  if (loading) return <div className="admin-page"><p style={{ color: '#94a3b8' }}>Loading…</p></div>;
  if (error)   return <div className="admin-page"><div className="alert alert-error">{error}</div></div>;
  if (!bundle) return null;

  const { account, capabilities, legacy_property, directory, rollup_30d, expense_rollup_30d } = bundle;

  return (
    <div className="admin-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 6 }}>
        <div>
          <button
            onClick={() => navigate('/accounts')}
            style={{ background: 'none', border: 'none', color: '#6d28d9', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 8 }}
          >
            ← Back to Accounts
          </button>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: '#0f172a' }}>{account.name}</h1>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <StatusPill status={account.status} />
            <AlnPill match={account.aln_match_status} />
            <SourcePill source={account.source} />
          </div>
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', textAlign: 'right' }}>
          accounts.id = {account.id}<br />
          {account.legacy_property_id && <>legacy_property_id = {account.legacy_property_id}<br /></>}
          {account.directory_uuid && <>directory_uuid = {account.directory_uuid}<br /></>}
          {account.aln_id && <>aln_id = {account.aln_id}</>}
        </div>
      </div>

      {/* Identity row */}
      <section style={{ marginTop: 20, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}>
        <h3 style={sectionTitle}>Identity</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
          <Field label="Address">{account.address || '—'}</Field>
          <Field label="City / Zip">{[account.city, account.zip].filter(Boolean).join(', ') || '—'}</Field>
          <Field label="Region">{account.region || '—'}</Field>
          <Field label="Units">{account.unit_count ?? '—'}</Field>
          <Field label="Management Co.">{account.mgmt_company_name || '—'}</Field>
          <Field label="Sales Rep">{account.sales_rep_name || '—'}</Field>
        </div>
      </section>

      {/* Capability map */}
      <section style={{ marginTop: 20 }}>
        <h3 style={sectionTitleOutside}>Capabilities</h3>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#64748b' }}>
          {account.status === 'active'
            ? 'This account is an active customer — all operational + CRM features are available.'
            : 'Prospect / churned accounts get CRM features only. Operational features unlock on conversion to active.'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <CapabilityTile label="Service Calls"   on={capabilities.service_calls} />
          <CapabilityTile label="Refunds"          on={capabilities.refunds_history} />
          <CapabilityTile label="No Charge"        on={capabilities.no_charge_history} />
          <CapabilityTile label="Parking Audits"   on={capabilities.audits_history} />
          <CapabilityTile label="Property Grading" on={capabilities.property_grading} />
          <CapabilityTile label="Expenses"         on={capabilities.expenses_history} />
          <CapabilityTile label="Notes"            on={capabilities.notes} comingSoon />
          <CapabilityTile label="Activities"       on={capabilities.activities} comingSoon />
          <CapabilityTile label="Meetings"         on={capabilities.meetings} comingSoon />
        </div>
      </section>

      {/* Operational rollup (active customers only) */}
      {rollup_30d && (
        <section style={{ marginTop: 28 }}>
          <h3 style={sectionTitleOutside}>Last 30 Days</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <Stat label="Service Calls"  value={rollup_30d.service_calls} />
            <Stat label="No Charge"      value={rollup_30d.no_charge}     color="#f59e0b" />
            <Stat label="Refunds"        value={rollup_30d.refunds}       color="#8b5cf6" />
            <Stat label="Take Backs"     value={rollup_30d.take_backs}    color="#3b82f6" />
            <Stat label="Audits"         value={rollup_30d.audits}        color="#0891b2" />
            <Stat label="Expense Count"  value={expense_rollup_30d.total_count} />
            <Stat
              label="Expense $"
              value={`$${(expense_rollup_30d.total_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              color="#16a34a"
            />
          </div>
        </section>
      )}

      {/* Directory metadata (when linked) */}
      {directory && (
        <section style={{ marginTop: 28, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}>
          <h3 style={sectionTitle}>Directory (ALN) Snapshot</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <Field label="Directory Name">{directory.property?.name || '—'}</Field>
            <Field label="Apollo Customer">{directory.property?.apollo_customer ? 'Yes' : 'No'}</Field>
            <Field label="Regional">{directory.regional?.name || '—'}</Field>
            <Field label="Mgmt Company">{directory.company?.name || '—'}</Field>
          </div>
        </section>
      )}

      {/* CRM coming-soon */}
      <section style={{ marginTop: 28 }}>
        <h3 style={sectionTitleOutside}>CRM (Coming Soon)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          {['Notes', 'Activities', 'To-dos', 'Meetings'].map(label => (
            <div key={label} style={{
              background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 12, padding: 20,
              minHeight: 100, color: '#94a3b8',
            }}>
              <div style={{ fontWeight: 700, color: '#64748b', marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: 12 }}>Coming in Step 3 — works for prospects and customers alike.</div>
            </div>
          ))}
        </div>
      </section>

      {/* Sign History — active-customer-only capability (panel name LOCKED).
          Fed by sign_work_attempts; each completed/attempted sign job at this
          property shows up here permanently. */}
      {account.status === 'active' && account.legacy_property_id && (
        <SignHistoryPanel propertyId={account.legacy_property_id} />
      )}

      {/* Legacy property linkage debug — only useful for super-admin during cutover */}
      {legacy_property && (
        <section style={{ marginTop: 28, background: '#0f172a08', border: '1px solid #cbd5e1', borderRadius: 12, padding: 20 }}>
          <h3 style={sectionTitle}>Legacy Property Linkage</h3>
          <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px' }}>
            This account mirrors <code>properties.id = {legacy_property.id}</code>.
            All operational logic still reads from the legacy row until cutover.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <Field label="Legacy Name">{legacy_property.name}</Field>
            <Field label="Legacy Region">{legacy_property.region}</Field>
            <Field label="Legacy Active">{legacy_property.is_active ? 'Yes' : 'No'}</Field>
          </div>
        </section>
      )}
    </div>
  );
}

const sectionTitle = {
  margin: '0 0 12px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: '#64748b',
};
const sectionTitleOutside = {
  ...sectionTitle,
  margin: '0 0 8px',
};

const SIGN_OUTCOMES = {
  completed: { label: 'Completed', color: '#166534', bg: '#dcfce7' },
  unable: { label: 'Unable', color: '#991b1b', bg: '#fee2e2' },
  more_time: { label: 'More Time Needed', color: '#92400e', bg: '#fef3c7' },
  rained_out: { label: 'Rained Out', color: '#1e40af', bg: '#dbeafe' },
};

function SignHistoryPanel({ propertyId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.getSignHistoryForProperty(propertyId).then(setRows).catch(() => setRows([]));
  }, [propertyId]);

  return (
    <section style={{ marginTop: 28, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}>
      <h3 style={sectionTitle}>Sign History</h3>
      {!rows && <div style={{ fontSize: 13, color: '#94a3b8' }}>Loading…</div>}
      {rows && rows.length === 0 && (
        <div style={{ fontSize: 13, color: '#94a3b8' }}>No sign work recorded at this property yet.</div>
      )}
      {rows && rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map(r => {
            const o = SIGN_OUTCOMES[r.outcome] || { label: r.outcome, color: '#475569', bg: '#f1f5f9' };
            return (
              <div key={r.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#64748b', width: 84 }}>
                  {(r.scheduled_date || r.created_at || '').slice(0, 10)}
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
                  background: o.bg, color: o.color, textTransform: 'uppercase', whiteSpace: 'nowrap',
                }}>{o.label}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{r.sign_work_summary || `Sign Request #${r.request_id}`}</div>
                  {r.comment && <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>“{r.comment}”</div>}
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    {r.tech_name || 'Unknown tech'} · Request #{r.request_id}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 500 }}>{children}</div>
    </div>
  );
}

function Stat({ label, value, color = '#0f172a' }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '14px 16px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    </div>
  );
}

function CapabilityTile({ label, on, comingSoon }) {
  const bg = on ? (comingSoon ? '#3b82f608' : '#10b98108') : '#94a3b808';
  const border = on ? (comingSoon ? '#bfdbfe' : '#bbf7d0') : '#e2e8f0';
  const fg = on ? (comingSoon ? '#1d4ed8' : '#059669') : '#94a3b8';
  const icon = on ? (comingSoon ? '🟦' : '✅') : '🔒';
  return (
    <div style={{
      background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '10px 12px',
      display: 'flex', alignItems: 'center', gap: 10, fontSize: 13,
    }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{ fontWeight: 600, color: fg }}>{label}</span>
    </div>
  );
}

const STATUS_STYLES = {
  active:   { bg: '#10b98118', fg: '#059669', label: 'Active' },
  prospect: { bg: '#3b82f618', fg: '#2563eb', label: 'Prospect' },
  churned:  { bg: '#94a3b818', fg: '#64748b', label: 'Churned' },
};
function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || { bg: '#94a3b818', fg: '#64748b', label: status || '—' };
  return <span style={pillStyle(s)}>{s.label}</span>;
}

const ALN_STYLES = {
  matched:    { bg: '#10b98118', fg: '#059669', label: 'ALN: Matched' },
  manual:     { bg: '#7c3aed18', fg: '#6d28d9', label: 'Manual' },
  unmatched:  { bg: '#f59e0b18', fg: '#b45309', label: 'ALN: Unmatched' },
  disputed:   { bg: '#dc262618', fg: '#b91c1c', label: 'ALN: Disputed' },
};
function AlnPill({ match }) {
  const s = ALN_STYLES[match] || ALN_STYLES.unmatched;
  return <span style={pillStyle(s)}>{s.label}</span>;
}

const SOURCE_STYLES = {
  properties_table: { bg: '#0f172a10', fg: '#334155', label: 'From: Properties' },
  directory:        { bg: '#0ea5e918', fg: '#0369a1', label: 'From: Directory' },
  manual:           { bg: '#7c3aed18', fg: '#6d28d9', label: 'From: Manual' },
  aln_api:          { bg: '#10b98118', fg: '#059669', label: 'From: ALN API' },
};
function SourcePill({ source }) {
  const s = SOURCE_STYLES[source] || SOURCE_STYLES.manual;
  return <span style={pillStyle(s)}>{s.label}</span>;
}

function pillStyle(s) {
  return {
    padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    background: s.bg, color: s.fg, textTransform: 'uppercase', letterSpacing: 0.4,
  };
}
